import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { DocDetails } from '../../../../api/types/docs/doc-details';
import { DocFeedbackStatusDB } from '../../../../api/types/docs/doc-feedback-status';
import { DocumentFeedbackDialog } from './components/document-feedback-dialog';

export const MyDocsStudents = observer(() => {
  const [selectedDocument, setSelectedDocument] = useState<DocDetails | null>(
    null,
  );
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  // Check if user profile exists and is a student
  if (!globalUserProfile.userProfile?.profile) {
    return <div>Loading user profile...</div>;
  }

  const studentProfile = globalUserProfile.userProfile
    .profile as StudentProfile;
  const docs: DocDetails[] = studentProfile.docs;
  const userId = globalUserProfile.userProfile.userWithRole.id;

  if (!docs || docs.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>המסמכים שלי</h2>
        <p>לא נמצאו מסמכים רשומים</p>
      </div>
    );
  }

  // Handlers
  const handleViewFeedback = (doc: DocDetails) => {
    setSelectedDocument(doc);
    setShowFeedbackDialog(true);
  };

  const handleFeedbackAdded = async () => {
    // Refresh the user profile data to get updated documents
    //await appApi.getHome2();
    globalUserProfile.getUserProfile();
    setShowFeedbackDialog(false);
    // The observer will automatically update the component when globalUserProfile changes
  };

  // Template functions for DataTable columns
  const docDescriptionTemplate = (doc: DocDetails) => {
    return doc.docMetadata.docDescription || '-';
  };

  const feedbackCountTemplate = (doc: DocDetails) => {
    return doc.feedbacks ? doc.feedbacks.length : 0;
  };

  const latestFeedbackStatusTemplate = (doc: DocDetails) => {
    if (!doc.feedbacks || doc.feedbacks.length === 0) {
      return <Tag value="אין משוב" severity="secondary" />;
    }

    // Get the latest feedback (assuming they are ordered by creation date)
    const latestFeedback = doc.feedbacks[doc.feedbacks.length - 1];

    switch (latestFeedback.status) {
      case DocFeedbackStatusDB.Approved:
        return <Tag value="אושר" severity="success" />;
      case DocFeedbackStatusDB.Reject:
        return <Tag value="נדחה" severity="danger" />;
      case DocFeedbackStatusDB.Init:
        return <Tag value="בבדיקה" severity="warning" />;
      default:
        return <Tag value="לא ידוע" severity="secondary" />;
    }
  };

  const createdAtTemplate = (doc: DocDetails) => {
    return new Date(doc.docMetadata.createdAt).toLocaleDateString('he-IL');
  };

  const createdByTemplate = (doc: DocDetails) => {
    return doc.docMetadata.createdByName || '-';
  };

  const actionsTemplate = (doc: DocDetails) => {
    return (
      <Button
        label="משוב"
        icon="pi pi-comment"
        size="small"
        onClick={() => handleViewFeedback(doc)}
      />
    );
  };

  return (
    <div>
      <DocumentFeedbackDialog
        visible={showFeedbackDialog}
        onHide={() => setShowFeedbackDialog(false)}
        document={selectedDocument}
        userId={userId}
        onFeedbackAdded={handleFeedbackAdded}
      />

      <h2>המסמכים שלי</h2>
      <DataTable
        value={docs}
        stripedRows
        showGridlines
        tableStyle={{ minWidth: '50rem' }}
        rows={10}
        style={{ marginTop: '16px' }}
      >
        <Column
          field="docMetadata.docName"
          header="שם המסמך"
          style={{ textAlign: 'right', fontWeight: 'bold' }}
        />
        <Column
          header="תיאור"
          body={docDescriptionTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="מספר משובים"
          body={feedbackCountTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סטטוס אחרון"
          body={latestFeedbackStatusTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="תאריך יצירה"
          body={createdAtTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="נוצר על ידי"
          body={createdByTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="פעולות"
          body={actionsTemplate}
          style={{ textAlign: 'center' }}
        />
      </DataTable>
    </div>
  );
});
